import Dayjs from 'dayjs'

import { linkCell } from './exportUtils'

// ONE purchase-order shape for Monthly Capex, shared by the centre's own
// download and HQ's row/board downloads (user 2026-08-25: "when we download the
// PO it should come like a PO, with links"). The register is
// # | Product | Category | Link | Qty | Rate | Total | Remarks under a PO header
// block; the Link column is the catalogue's reference_link as a real hyperlink
// in both Excel and the PDF, so whoever places the order opens the exact product
// page the item was priced from.
//
// 'Rs' not '₹' anywhere the PDF renders — the jsPDF built-in font has no rupee
// glyph and prints a blank box.

export const CAPEX_PO_HEADERS = [
  '#',
  'Product',
  'Category',
  'Link',
  'Qty',
  'Rate (Rs)',
  'Total (Rs)',
  'Remarks'
]

export const CAPEX_PO_ALIGN = [
  'right',
  false,
  false,
  false,
  'right',
  'right',
  'right',
  false
]

// lines: [{ reference_id, name, category, reference_link, qty, unit_price,
//           line_total, centre_remarks }] — the shape the capex sheet endpoint
// already returns, so both callers pass their rows straight through.
export const capexPoRows = (lines) =>
  lines.map((it, i) => [
    i + 1,
    it.reference_id ? `${it.name} (${it.reference_id})` : it.name,
    it.category || '—',
    linkCell(it.reference_link),
    Number(it.qty),
    Math.round(Number(it.unit_price || it.cost || 0) * 100) / 100,
    Math.round(Number(it.line_total || 0)),
    it.centre_remarks || ''
  ])

// Numbers, not strings — the PDF stringifies them anyway, and Excel keeps them
// summable instead of landing a text cell at the foot of a money column.
export const capexPoTotals = (rows) => [
  'Total',
  null,
  null,
  null,
  rows.reduce((s, r) => s + (Number(r[4]) || 0), 0),
  null,
  Math.round(rows.reduce((s, r) => s + (Number(r[6]) || 0), 0)),
  null
]

// The block Excel gets above the table — the same facts the PDF prints as its
// title line and meta band.
export const capexRegisterPre = ({
  title,
  po,
  centreName,
  monthLabel,
  budget,
  submittedBy
}) =>
  [
    [title || 'Monthly Capex order'],
    ['PO', po || ''],
    ['Centre', centreName || ''],
    ['Month', monthLabel || ''],
    budget != null ? ['Budget', Number(budget)] : null,
    submittedBy ? ['Submitted by', submittedBy] : null,
    []
  ].filter(Boolean)

// The full PDF spec for one centre's order (renderOrder in exportUtils).
export const capexOrderSpec = ({
  lines,
  centreName,
  monthLabel,
  po,
  budget,
  submittedAt,
  submittedBy,
  title,
  extraMeta
}) => {
  const rows = capexPoRows(lines)
  return {
    title: title || 'Monthly Capex order',
    subtitle: `${centreName || ''} — ${monthLabel}`,
    po: po || undefined,
    meta: [
      { k: 'Centre', v: centreName },
      { k: 'Month', v: monthLabel },
      submittedAt
        ? { k: 'Submitted', v: Dayjs(submittedAt).format('DD MMM YYYY') }
        : null,
      submittedBy ? { k: 'Submitted by', v: submittedBy } : null,
      budget != null ? { k: 'Budget', v: `Rs ${Number(budget)}` } : null,
      ...(extraMeta || [])
    ].filter(Boolean),
    headers: CAPEX_PO_HEADERS,
    rows,
    align: CAPEX_PO_ALIGN,
    totals: capexPoTotals(rows)
  }
}

// The Excel worksheet for the same order.
export const capexOrderSheet = (opts) => {
  const rows = capexPoRows(opts.lines)
  return {
    name: opts.sheetName || opts.centreName || 'Capex',
    pre: capexRegisterPre(opts),
    headers: CAPEX_PO_HEADERS,
    rows: [...rows, [], capexPoTotals(rows)]
  }
}
